import { CurrentUser } from './types';

export const handleInvite = (
  emails: string,
  setIsSubmitting: (value: boolean) => void,
  setError: (value: string | null) => void,
  currentUser: CurrentUser,
  setExternalDomainWarning: (value: string | null) => void,
  setPendingInvitations: (value: Array<{ email: string; accessLevel: string }>) => void,
  setShowExternalConfirmation: (value: boolean) => void,
  accessLevel: string,
  sendInvitations: (invitations: Array<{ email: string; accessLevel: string }>) => Promise<void>,
): void => {
  if (emails.trim() === '') {
    setError('Please enter at least one email address');
    return;
  }

  setIsSubmitting(true);
  setError(null);

  // Get the inviter's email domain
  const inviterEmail = currentUser.emails?.nodes?.[0]?.email ?? '';
  const inviterDomain = inviterEmail.split('@')[1] ?? '';

  // Parse and validate emails
  const emailList = emails
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email);

  if (emailList.length === 0) {
    setError('Please enter valid email addresses');
    setIsSubmitting(false);
    return;
  }

  // Check if the number of emails exceeds the limit (10)
  if (emailList.length > 10) {
    setError('You can only invite up to 10 users at a time. Please reduce the number of email addresses.');
    setIsSubmitting(false);
    return;
  }

  // Check for external domains
  if (inviterDomain !== '') {
    const externalEmails = emailList.filter((email) => {
      const domain = email.split('@')[1] ?? '';
      return domain !== '' && domain.toLowerCase() !== inviterDomain.toLowerCase();
    });

    if (externalEmails.length > 0) {
      // Store the warning message
      setExternalDomainWarning(
        `You're inviting ${externalEmails.length} user(s) from outside your organization (${inviterDomain}).`,
      );

      // Store pending invitations and show confirmation dialog
      const invitations = emailList.map((email) => ({
        email,
        accessLevel,
      }));
      setPendingInvitations(invitations);
      setShowExternalConfirmation(true);
      setIsSubmitting(false);
      return; // Pause the invitation process
    } else {
      setExternalDomainWarning(null);
    }
  }

  // If no external domains or confirmation already given, proceed with invitation
  const invitations = emailList.map((email) => ({
    email,
    accessLevel,
  }));

  void sendInvitations(invitations);
};
